const OVERLAY_ID = "native-listing-hero-overlay";
const EXPAND_MS = 320;
const EXIT_MS = 200;

let activeOverlay: HTMLElement | null = null;

function removeOverlay() {
  const el = activeOverlay ?? document.getElementById(OVERLAY_ID);
  if (!el) return;
  el.remove();
  activeOverlay = null;
  document.body.classList.remove("native-listing-hero-active");
}

/** Fade out hero shell once CarDetails gallery has mounted. */
export function dismissListingHeroTransition() {
  const el = activeOverlay ?? document.getElementById(OVERLAY_ID);
  if (!el) return;

  el.classList.add("native-listing-hero-transition--exit");
  window.setTimeout(removeOverlay, EXIT_MS);
}

function preloadImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
}

function resolveHeroImage(
  sourceEl: HTMLElement,
  thumbnailUrl: string,
): HTMLImageElement {
  const existing = sourceEl.querySelector("img");
  if (
    existing instanceof HTMLImageElement &&
    existing.complete &&
    existing.naturalWidth > 0
  ) {
    const clone = existing.cloneNode(true) as HTMLImageElement;
    clone.className = "native-listing-hero-transition__img";
    clone.removeAttribute("loading");
    return clone;
  }

  const img = document.createElement("img");
  img.className = "native-listing-hero-transition__img";
  img.src = thumbnailUrl;
  img.alt = "";
  return img;
}

function waitForImage(img: HTMLImageElement): Promise<void> {
  if (img.complete && img.naturalWidth > 0) return Promise.resolve();
  return new Promise((resolve) => {
    img.onload = () => resolve();
    img.onerror = () => resolve();
  });
}

function createSkeletonBlock(className: string): HTMLDivElement {
  const block = document.createElement("div");
  block.className = `native-listing-hero-skeleton-block ${className}`;
  return block;
}

function createSkeletonPanel(heroBottom: number): HTMLDivElement {
  const panel = document.createElement("div");
  panel.className = "native-listing-hero-transition__skeleton";
  panel.style.top = `${heroBottom}px`;

  const title = createSkeletonBlock("native-listing-hero-skeleton-title");
  const subtitle = createSkeletonBlock("native-listing-hero-skeleton-subtitle");
  const price = createSkeletonBlock("native-listing-hero-skeleton-price");

  const chips = document.createElement("div");
  chips.className = "native-listing-hero-skeleton-chips";
  for (let i = 0; i < 4; i += 1) {
    chips.appendChild(createSkeletonBlock("native-listing-hero-skeleton-chip"));
  }

  const section = createSkeletonBlock("native-listing-hero-skeleton-section");
  const row = document.createElement("div");
  row.className = "native-listing-hero-skeleton-row";
  row.appendChild(createSkeletonBlock("native-listing-hero-skeleton-half"));
  row.appendChild(createSkeletonBlock("native-listing-hero-skeleton-half"));

  panel.append(title, subtitle, price, chips, section, row);
  return panel;
}

function readBorderRadius(el: HTMLElement): string {
  const radius = getComputedStyle(el).borderRadius;
  return radius && radius !== "0px" ? radius : "0.75rem";
}

/**
 * Airbnb-style expand: card image grows to gallery header, then navigate.
 * Preloads/clones source image to avoid thumbnail pop-in.
 */
export async function playListingHeroTransition(
  sourceEl: HTMLElement,
  thumbnailUrl: string,
  navigateFn: () => void,
): Promise<void> {
  removeOverlay();

  const rect = sourceEl.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) {
    navigateFn();
    return;
  }

  const existingImg = sourceEl.querySelector("img");
  if (
    !(existingImg instanceof HTMLImageElement && existingImg.complete)
  ) {
    await preloadImage(thumbnailUrl);
  }

  const img = resolveHeroImage(sourceEl, thumbnailUrl);
  await waitForImage(img);

  const overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.className = "native-listing-hero-transition";
  overlay.setAttribute("aria-hidden", "true");

  const backdrop = document.createElement("div");
  backdrop.className = "native-listing-hero-transition__backdrop";

  const hero = document.createElement("div");
  hero.className = "native-listing-hero-transition__hero";
  hero.style.left = `${rect.left}px`;
  hero.style.top = `${rect.top}px`;
  hero.style.width = `${rect.width}px`;
  hero.style.height = `${rect.height}px`;
  hero.style.borderRadius = readBorderRadius(sourceEl);

  const shade = document.createElement("div");
  shade.className = "native-listing-hero-transition__shade";
  hero.appendChild(img);
  hero.appendChild(shade);

  overlay.appendChild(backdrop);

  const targetHeight = Math.min(window.innerHeight * 0.46, 420);
  const skeleton = createSkeletonPanel(targetHeight);
  overlay.appendChild(skeleton);
  overlay.appendChild(hero);

  document.body.appendChild(overlay);
  document.body.classList.add("native-listing-hero-active");
  activeOverlay = overlay;

  await new Promise<void>((resolve) => {
    let navigated = false;

    const finishExpand = () => {
      if (navigated) return;
      navigated = true;
      navigateFn();
      resolve();
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlay.classList.add("native-listing-hero-transition--expanded");
        hero.style.left = "0px";
        hero.style.top = "0px";
        hero.style.width = `${window.innerWidth}px`;
        hero.style.height = `${targetHeight}px`;
        hero.style.borderRadius = "0px";
        skeleton.style.top = `${targetHeight}px`;
      });
    });

    hero.addEventListener("transitionend", finishExpand, { once: true });
    window.setTimeout(finishExpand, EXPAND_MS + 40);
  });
}
